import { observer } from 'mobx-react';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';

import {
  AccountsPayableLinkStatus,
  AccountsPayableLinkUpdateStatus,
} from '@constants/accountsPayableLink';
import useStore from '@hooks/useStore';

import AccountsPayableCustomTools from '@components/AccountsPayableCustomTools';
import AccountsPayableCustomTable from '@components/AccountsPayableCustomTable';
import AccountsPayableGroupCustomTables from '@components/AccountsPayableGroupCustomTables';
import OysterButton from '@components/common/Button';

import useAccountsPayable from '@hooks/useAccountsPayable';

import { formattedAmount } from '@utils/format';

import useStyles from './../styles';
import { toJS } from 'mobx';

export interface IValidation {
  accountsPayableLinkId: string;
}

const Validation: FC<IValidation> = ({ accountsPayableLinkId }) => {
  const { t } = useTranslation();
  const { accountsPayableLink } = useStore();
  const { fundInfo, isSubmitting, tools, accountsPayable, partnerName } = accountsPayableLink;
  const { getTotalAmount, getUnselectedIdsLength, getCanProcessLength, canProcess, canNotProcess } =
    accountsPayable;
  const { openCancelOperationModal, openConfirmOperationModal, handleError } = useAccountsPayable();
  const { toolsGroupBy } = tools;
  const css = useStyles();

  const rows = [...toJS(canProcess), ...toJS(canNotProcess)];

  const fetchConfirm = async () => {
    try {
      await accountsPayableLink.updateStatus(AccountsPayableLinkUpdateStatus.CONFIRMED);
      accountsPayableLink.setCurrentStatus(AccountsPayableLinkStatus.CONFIRMED);
    } catch (error) {
      handleError(error as { message: string });
    }
  };

  const handleConfirm = async () => {
    if (getUnselectedIdsLength > 0) {
      await openConfirmOperationModal(getUnselectedIdsLength, partnerName, accountsPayableLinkId);
    } else {
      await fetchConfirm();
    }
  };

  return (
    <div className={css.card}>
      <AccountsPayableCustomTools />
      {toolsGroupBy !== '' ? (
        <AccountsPayableGroupCustomTables
          withSubtotals
          toolBarTitle={'Validación de pagos'}
          rows={rows}
        />
      ) : (
        <AccountsPayableCustomTable toolBarTitle={'Validación de pagos'} rows={rows} />
      )}
      <Grid container spacing={3} className={css.containerButtons}>
        <Grid item xs={12} md={8} />
        <Grid item xs={12} md={4}>
          <Grid container className={css.containerAmount}>
            <Grid item xs={8} md={6}>
              Total a dispersar{' '}
              <span className={css.rowsCounter}>
                {`${getCanProcessLength - getUnselectedIdsLength} de ${getCanProcessLength}`}
              </span>
            </Grid>
            <Grid item xs={4} md={6} className={css.totalAmountContainer}>
              {formattedAmount(getTotalAmount, fundInfo.currency)}{' '}
              <span className={css.totalAmountCurrency}>{fundInfo.currency}</span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={8} />
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={6}>
              <OysterButton
                className={css.buttonSecondary}
                fullWidth
                cancel
                onClick={openCancelOperationModal}
                label={t('accountsPayable.cancel')}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <OysterButton
                fullWidth
                label={t('accountsPayable.continue')}
                onClick={handleConfirm}
                loading={isSubmitting}
                disabled={isSubmitting || getTotalAmount === 0}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default observer(Validation);
